// Updated AuthContext.js with email-code authentication

import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import activeAgentMonitor from '../services/activeAgentMonitor';
import globalWebSocketService from '../services/globalWebSocketService';

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // Add error handling for when context is undefined
  if (context === undefined) {
    console.error('useAuth must be used within an AuthProvider');
    // Return default values instead of undefined to prevent destructuring errors
    return {
      currentUser: null,
      isAuthenticated: false,
      loading: false,
      loginWithEmail: () => Promise.reject('AuthContext not initialized'),
      registerWithEmail: () => Promise.reject('AuthContext not initialized'),
      verifyCode: () => Promise.reject('AuthContext not initialized'),
      logout: () => {},
      updateProfile: () => Promise.reject('AuthContext not initialized')
    };
  }
  
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setLoading(false);
          return;
        }
        
        try {
          const response = await api.get('/users/me');
          setCurrentUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
        } finally {
          setLoading(false);
        }
      } catch (e) {
        console.error('Error checking auth status:', e);
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  // Login with email (send verification code)
  const loginWithEmail = async (email) => {
    try {
      const response = await api.post('/auth/login/request-code', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send login code');
    }
  };

  // Register with email (send verification code)
  const registerWithEmail = async (email) => {
    try {
      const response = await api.post('/auth/register/request-code', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send registration code');
    }
  };

  // Verify code and complete authentication
  const verifyCode = async (email, code) => {
    try {
      const response = await api.post('/auth/verify-code', { email, code });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      return user;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Invalid verification code');
    }
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      const response = await api.put('/users/me', userData);
      
      const updatedUser = response.data;
      setCurrentUser(updatedUser);
      
      return updatedUser;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update profile');
    }
  };

  // Logout function
  const logout = () => {
    // Stop monitoring active agents
    console.log('[AuthContext] User logging out, stopping agent monitoring');
    activeAgentMonitor.stopMonitoring();
    
    // Stop all WebSocket connections
    globalWebSocketService.stopAllConnections();
    
    localStorage.removeItem('token');
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // Refresh user data
  const refreshUserData = async () => {
    try {
      const response = await api.get('/users/me');
      setCurrentUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw new Error(error.response?.data?.error || 'Failed to refresh user data');
    }
  };

  // Auth context value
  const value = {
    currentUser,
    isAuthenticated,
    loading,
    loginWithEmail,
    registerWithEmail,
    verifyCode,
    logout,
    updateProfile,
    refreshUserData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;